/**
 * Add closet (PRP-SKETCH-FIELD-3 J8, H6). One undo step (`History.run`).
 *
 * The tap point is the closet's centre along the wall, clamped so the closet stays
 * on it; a closet wider than the wall takes the whole wall (the H8 short-wall rule).
 * Sizes are the closet's inside (interior faces), like every room.
 *
 * - Outside: a new room "Closet n" one wall thickness beyond the wall, width along
 *   it, depth outward: a drag-out (E10) with fixed sizes.
 * - Inside: carved from the room. The room gets a notch the closet's depth plus the
 *   plan's interior wall deep, with a side wall either side of the closet (a side at
 *   the room's corner is the room's own wall, so the notch runs to it). The closet
 *   fills the notch beyond a shared wall. The room's measurements are kept; the notch
 *   corners are added as measured from the closet's fixed size.
 *
 * The door goes on the shared wall, centred: `closet_default.door` with its panels,
 * the closet width less the jamb clearance either side (the doors' short-wall rule,
 * `shortWallFit`), made whole by `doorWithDefaults` (cased per kind, painted unless
 * stained). A closet narrower than `min_bypass_width_ft` gets the fallback swing
 * door. Both closets record the tapped wall as `attachedEdge` (J18).
 */
import { CLOSET_OUTSIDE, IN_PER_FT, MANUAL_SOURCE, POLICY, inToFt, type ClosetPlacement } from './policy';
import { doorWithDefaults, stainedDefault } from './doors';
import { shortWallFit } from './doorsFit';
import { EPS, axisOf, clamp, dist, edgeOf, findRoom, outN, round6, snapIn, tidy, unit, vIdx } from './geom';
import { uid } from './plan';
import { makeRoom, nextName, roomOffWall } from './rooms';
import { roomDefaults, type RoomType } from './roomDefaults';
import { setWallIn, wallIn } from './thickness';
import { addOpeningAt, deleteOpening } from './openings';
import { findOpposite } from './measure';
import { noteCheckDims } from './walls';
import type { Axis, Corner, DoorV2, Fail, Measurement, Opening, Plan, Pt, Room } from './types';

export interface ClosetOpts {
  widthFt?: number;
  depthFt?: number;
  placement?: ClosetPlacement;
  /** The user's stained default for interior doors (J11); else the plan's (`stainedDefault`). */
  stained?: boolean;
  /** The new room's id (the screen's UUID). */
  id?: string;
  /**
   * PRP-WEB-2 W1.4: a `room_defaults` type (`reach_in_closet`, `walk_in_closet`, `linen`,
   * `pantry`) whose census size and name the closet takes where `widthFt` / `depthFt` are
   * not given. Absent: `closet_default`'s size and `closet_room_name` (the phone, unchanged).
   */
  type?: RoomType;
}

export type ClosetResult =
  | { ok: true; rid: string; doorId: number; placement: ClosetPlacement; widthFt: number; depthFt: number }
  | Fail<'missing' | 'not-straight' | 'invalid' | 'too-deep'>;

/**
 * The closet's door for an inside width of `w` feet (J8, H8, J13), built with the
 * doors' helpers: the kind's width wanted (the whole closet for the bypass, the
 * fallback's width otherwise) less a jamb each side when the wall is short for it
 * (`shortWallFit`), and the door fields made whole for the kind (`doorWithDefaults`).
 */
export function closetDoor(w: number, stained?: boolean): { widthFt: number; door: DoorV2 } {
  const cd = POLICY.closet_default;
  const bypass = w >= cd.min_bypass_width_ft - EPS;
  const type = bypass ? cd.door : cd.fallback_door.type;
  const want = bypass ? w : cd.fallback_door.width_ft;
  const fit = shortWallFit(w, 'door', want);
  const door = doorWithDefaults({
    type, out: false, hingeAtEnd: false, stained: stained ?? POLICY.stained_default,
    ...(bypass ? { panels: cd.panels } : {}),
  });
  return { widthFt: round6(fit ? fit.widthFt : want), door };
}

const len = (axis: Axis, a: number, b: number, valueFt: number): Measurement => ({ axis, a, b, valueFt, kind: 'len', source: MANUAL_SOURCE });

/** Add a closet on wall `vid` of room `rid`, centred at `atFt` along it. */
export function addCloset(plan: Plan, rid: string, vid: number, atFt: number, opts: ClosetOpts = {}): ClosetResult {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const t = axisOf(E.a, E.b);
  if (t !== 'h' && t !== 'v') return { ok: false, err: 'not-straight', message: 'Pick a straight wall' };
  const cd = POLICY.closet_default;
  const L = dist(E.a, E.b);
  // W1.4: census sizes by type (data); the phone's sheet always passes its own sizes.
  const rd = opts.type ? roomDefaults(opts.type) : null;
  if (opts.type && !rd) return { ok: false, err: 'invalid', message: `Unknown closet type: ${opts.type}` };
  const W = snapIn(Math.min(opts.widthFt ?? rd?.widthFt ?? cd.width_ft, L));
  const D = snapIn(opts.depthFt ?? rd?.depthFt ?? cd.depth_ft);
  if (!(W >= POLICY.opening_min_width_ft) || !(D >= POLICY.across_min_ft)) {
    return { ok: false, err: 'invalid', message: 'Enter the closet width and depth' };
  }
  const s = clamp(snapIn(atFt - W / 2), 0, Math.max(0, L - W));
  const placement = opts.placement ?? cd.placement;
  const door = closetDoor(W, opts.stained ?? stainedDefault(plan, 'interior'));
  const name = nextName(plan, rd ? rd.name : POLICY.closet_room_name);
  let closet: Room, wall: { room: Room; vid: number; off: number };
  if (placement === CLOSET_OUTSIDE) {
    closet = roomOffWall(plan, r, vid, s, [[0, 0], [W, 0], [W, D], [0, D]], name, opts.id);
    wall = { room: r, vid, off: s };
  } else {
    const carved = carve(plan, r, vid, s, W, D, name, opts.id);
    if (!carved.ok) return carved;
    closet = carved.closet;
    wall = carved.wall;
  }
  closet.ceilingFt = r.ceilingFt;
  closet.level = r.level;
  delete closet.ceilingFromDefault;
  const along: Axis = t === 'h' ? 'x' : 'y', across: Axis = t === 'h' ? 'y' : 'x';
  const [c0, c1, c2] = closet.corners;
  closet.measurements.push(len(along, c0.id, c1.id, W), len(across, c1.id, c2.id, D));
  closet.attachedEdge = c0.id;
  plan.rooms.push(closet);
  const res = addOpeningAt(plan, wall.room, wall.vid, round6(wall.off + W / 2 - door.widthFt / 2), { kind: 'door', ...door });
  tidy(plan);
  return { ok: true, rid: closet.id, doorId: res.oid, placement, widthFt: W, depthFt: D };
}

/**
 * The inside closet: notch room `r` on wall `vid` for a closet `W` x `D` starting
 * `s` along the wall, and build the closet. Returns where its door goes (the
 * room's notch face, offset of the closet's start along it).
 */
function carve(
  plan: Plan, r: Room, vid: number, s0: number, W: number, D: number, name: string, id?: string,
): { ok: true; closet: Room; wall: { room: Room; vid: number; off: number } } | Fail<'too-deep'> {
  const E = edgeOf(r, vid)!;
  const L = dist(E.a, E.b), u = unit(E.a, E.b), n = outN(r, E.a, E.b);
  const tIn = plan.defaults.interiorWallIn ?? POLICY.interior_wall_in, T = inToFt(tIn);
  const Dn = D + T;
  const opp = findOpposite(r, vid);
  if (!opp || opp.d - Dn < POLICY.room_min_ft - EPS) {
    return { ok: false, err: 'too-deep', message: `The closet is too deep for ${r.name}` };
  }
  // A side wall needs its thickness: closer to a corner than that, the closet goes to the corner.
  let s = s0;
  if (s > EPS && s < T) s = 0;
  if (L - W - s > EPS && L - W - s < T) s = L - W;
  const atStart = s < EPS, atEnd = L - W - s < EPS;
  const n0 = atStart ? 0 : s - T, n1 = atEnd ? L : s + W + T;
  const o = { x: E.a.x, y: E.a.y }; // the wall's start before any corner moves
  const P = (along: number, depth: number): Pt => ({
    x: round6(o.x + u.x * along - n.x * depth), y: round6(o.y + u.y * along - n.y * depth),
  });
  const across: Axis = axisOf(E.a, E.b) === 'h' ? 'y' : 'x', alongAx: Axis = across === 'x' ? 'y' : 'x';
  const oldT = wallIn(r, vid);
  const prevId = r.corners[(vIdx(r, vid) - 1 + r.corners.length) % r.corners.length].id;
  const endT = wallIn(r, E.b.id), startT = wallIn(r, prevId);
  const a = E.a, b = E.b;
  const corner = (p: Pt): Corner => ({ id: uid(plan), ...p });
  // Openings on the wall: kept beyond the notch (moved to the remainder), dropped inside it.
  const onWall = r.openings.filter((o) => o.edgeStart === vid);
  const ins: Corner[] = [];
  let faceId = a.id, remainder: Corner | null = null;
  if (!atStart) {
    const k0 = corner(P(n0, 0)), k1 = corner(P(n0, Dn));
    ins.push(k0, k1);
    setWallIn(r, k0.id, tIn);
    setWallIn(r, k1.id, tIn);
    faceId = k1.id;
    r.measurements.push(len(alongAx, a.id, k0.id, n0), len(across, k0.id, k1.id, Dn));
  }
  if (!atEnd) {
    const k2 = corner(P(n1, Dn)), k3 = corner(P(n1, 0));
    ins.push(k2, k3);
    setWallIn(r, k2.id, tIn);
    setWallIn(r, k3.id, oldT);
    remainder = k3;
    r.measurements.push(len(across, k3.id, k2.id, Dn)); // ties the far side of the notch back to the wall's line
  }
  r.corners.splice(vIdx(r, vid) + 1, 0, ...ins);
  const moved: number[] = [];
  if (atStart) { Object.assign(a, P(0, Dn)); setWallIn(r, a.id, tIn); moved.push(a.id); }
  if (atEnd) { Object.assign(b, P(L, Dn)); moved.push(b.id); }
  const faceEnd = r.corners[(vIdx(r, faceId) + 1) % r.corners.length];
  r.measurements.push(len(alongAx, faceId, faceEnd.id, n1 - n0));
  // The room's sizes on the notched axis stay on the wall's line (J8).
  const line = [a, b].filter((c) => !moved.includes(c.id));
  r.measurements = r.measurements.map((m) => {
    if (m.axis !== across || !line.length) return m;
    const to = line[0].id;
    return { ...m, a: moved.includes(m.a) ? to : m.a, b: moved.includes(m.b) ? to : m.b };
  }).filter((m) => m.a !== m.b);
  if (!line.length) noteCheckDims(r, `The closet took the whole wall; ${r.name} is ${Number((Dn * IN_PER_FT).toFixed(2))}" shorter`);
  for (const o of onWall) moveOrDrop(plan, r, o, n0, n1, atStart, remainder);
  // The closet: inside faces, first wall on the tapped wall's line.
  const closet = makeRoom(plan, name, [[s, 0], [s + W, 0], [s + W, D], [s, D]].map(([x, y]) => {
    const p = P(x, y);
    return [p.x, p.y] as [number, number];
  }), { raw: true, id });
  const [c0, c1, c2, c3] = closet.corners;
  setWallIn(closet, c0.id, oldT);
  setWallIn(closet, c1.id, atEnd ? endT : tIn);
  setWallIn(closet, c2.id, tIn);
  setWallIn(closet, c3.id, atStart ? startT : tIn);
  return { ok: true, closet, wall: { room: r, vid: faceId, off: s - n0 } };
}

/** An opening on the notched wall: beyond the notch it moves to the remainder; inside it, it goes. */
function moveOrDrop(plan: Plan, r: Room, o: Opening, n0: number, n1: number, atStart: boolean, rem: Corner | null): void {
  const s0 = o.offFt, s1 = o.offFt + o.widthFt;
  if (!atStart && s1 <= n0 + EPS) return;
  if (rem && s0 >= n1 - EPS) { o.edgeStart = rem.id; o.offFt = s0 - n1; return; }
  deleteOpening(plan, r.id, o.id);
}
